/**
 * PushHealth — V1.9.18
 *
 * Admin/superadmin diagnostic panel for Web Push. Shows:
 *   • Whether VAPID env vars are configured on this backend deploy
 *   • Whether pywebpush is installed
 *   • The signed-in admin's push subscription count + endpoints
 *   • Global subscription count across all users
 *   • A "Send test push to my device" button that fires POST
 *     /api/me/push/test and reports the send result (sent / skipped /
 *     stale / reason).
 *
 * Useful when a user reports push notifications aren't firing on their
 * phone — you can validate the whole pipeline without leaving the app.
 */

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRinging, CheckCircle, XCircle, ArrowClockwise, PaperPlaneTilt, Warning, Fingerprint } from "@phosphor-icons/react";
import api from "@/lib/api";
import usePushSubscription from "@/hooks/usePushSubscription";
import { useAuth } from "@/context/AuthContext";

const Row = ({ label, value, ok }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-ink/5 last:border-b-0">
        <span className="text-xs font-bold uppercase tracking-wider text-ink/60">{label}</span>
        <span
            className={`text-xs font-bold inline-flex items-center gap-1 ${
                ok === true ? "text-emerald-600" : ok === false ? "text-red-600" : "text-ink/80"
            }`}
        >
            {ok === true && <CheckCircle size={14} weight="fill" />}
            {ok === false && <XCircle size={14} weight="fill" />}
            <span className="truncate max-w-[240px]">{value ?? "—"}</span>
        </span>
    </div>
);

export default function PushHealth() {
    const { user } = useAuth();
    const {
        supported,
        permission,
        subscribed,
        requestPermission,
    } = usePushSubscription(user);
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/push/diagnostic");
            setDiag(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to load diagnostic.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const sendTest = async () => {
        if (!subscribed) {
            const ok = await requestPermission();
            if (!ok) {
                toast.error("Enable notifications first to test.");
                return;
            }
        }
        setTesting(true);
        try {
            const res = await api.post("/me/push/test");
            const d = res.data;
            if (d.sent > 0) {
                toast.success(`Test push sent (${d.sent} device${d.sent > 1 ? "s" : ""}). Check your phone/desktop.`);
            } else if (d.reason) {
                toast.error(`Push not sent: ${d.reason}`);
            } else if (d.skipped > 0) {
                toast.error(`All ${d.skipped} subscription(s) were stale (404/410). Re-subscribe on your device.`);
            } else {
                toast.error("No subscriptions found for your account.");
            }
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Test push failed.");
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="p-6 max-w-3xl mx-auto" data-testid="push-health-page">
            <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-ink/40">
                        System · Diagnostics
                    </p>
                    <h1 className="font-heading text-2xl font-black tracking-tight mt-1">
                        Push Notification Health
                    </h1>
                    <p className="text-sm text-ink/60 mt-1 max-w-xl">
                        Verify VAPID keys, check subscriptions, and fire a test push at your own device.
                        Use this whenever a user reports missing lock-screen notifications.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    data-testid="push-health-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/20 text-xs font-black uppercase tracking-wider hover:border-cobalt hover:text-cobalt disabled:opacity-40 rounded-md"
                >
                    <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            {loading ? (
                <p className="text-xs text-ink/50 py-8 text-center">Loading diagnostic…</p>
            ) : diag ? (
                <>
                    {/* Backend config */}
                    <section className="bg-white border border-ink/10 rounded-md p-5 mb-5" data-testid="push-health-backend">
                        <div className="flex items-center gap-2 mb-3">
                            <Fingerprint size={16} weight="bold" className="text-cobalt" />
                            <h2 className="font-heading font-black">Backend configuration</h2>
                        </div>
                        <Row
                            label="VAPID public key"
                            value={diag.vapid.public_key_prefix || "not set"}
                            ok={diag.vapid.public_key_set}
                        />
                        <Row
                            label="VAPID private key"
                            value={diag.vapid.private_key_set ? "configured" : "not set"}
                            ok={diag.vapid.private_key_set}
                        />
                        <Row
                            label="VAPID subject"
                            value={diag.vapid.subject || "using default"}
                            ok={!!diag.vapid.subject}
                        />
                        <Row
                            label="pywebpush installed"
                            value={diag.pywebpush_installed ? "yes" : "missing"}
                            ok={diag.pywebpush_installed}
                        />
                        {!diag.vapid.public_key_set && (
                            <div className="mt-3 border border-red-200 bg-red-50 text-red-700 text-xs p-3 rounded-md flex gap-2">
                                <Warning size={16} weight="fill" className="shrink-0" />
                                <div>
                                    VAPID env vars are missing. Set <code>VAPID_PUBLIC_KEY</code>, <code>VAPID_PRIVATE_KEY</code>
                                    &nbsp;and <code>VAPID_SUBJECT</code> on this deployment then restart. No push notifications
                                    can be sent until that's fixed.
                                </div>
                            </div>
                        )}
                    </section>

                    {/* This device */}
                    <section className="bg-white border border-ink/10 rounded-md p-5 mb-5" data-testid="push-health-device">
                        <div className="flex items-center gap-2 mb-3">
                            <BellRinging size={16} weight="bold" className="text-cobalt" />
                            <h2 className="font-heading font-black">This device / browser</h2>
                        </div>
                        <Row label="Web Push supported" value={supported ? "yes" : "no"} ok={supported} />
                        <Row
                            label="Notification permission"
                            value={permission}
                            ok={permission === "granted"}
                        />
                        <Row
                            label="Push subscription active on this device"
                            value={subscribed ? "yes" : "no"}
                            ok={subscribed}
                        />
                    </section>

                    {/* Server-side subscriptions */}
                    <section className="bg-white border border-ink/10 rounded-md p-5 mb-5" data-testid="push-health-subs">
                        <div className="flex items-center justify-between gap-2 mb-3">
                            <h2 className="font-heading font-black">Subscriptions on file for your account</h2>
                            <span className="text-xs font-bold text-ink/50">
                                Global: {diag.global_subscriptions}
                            </span>
                        </div>
                        {diag.my_subscriptions === 0 ? (
                            <div className="text-xs text-ink/60 border border-dashed border-ink/15 p-4 rounded-md bg-cream/40">
                                No subscriptions stored server-side. Tap the button below (or "Turn on"
                                on any chat page banner) to enable and create one.
                            </div>
                        ) : (
                            <ul className="text-xs divide-y divide-ink/5">
                                {(diag.my_subscription_endpoints || []).map((e, i) => (
                                    <li key={i} className="py-2 flex flex-col gap-0.5">
                                        <span className="font-bold text-ink">
                                            {e.host || "unknown endpoint"}
                                        </span>
                                        <span className="text-ink/60 truncate">
                                            {e.ua || "no user-agent recorded"}
                                        </span>
                                        <span className="text-ink/40 text-[10px]">
                                            Added {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Test */}
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={sendTest}
                            disabled={testing || !diag.vapid.public_key_set}
                            data-testid="push-health-send-test"
                            className="btn-gradient inline-flex items-center gap-2 px-6 py-3 text-xs font-black uppercase tracking-wider disabled:opacity-40 rounded-md"
                        >
                            <PaperPlaneTilt size={14} weight="bold" />
                            {testing ? "Sending…" : "Send test push to my device"}
                        </button>
                    </div>
                </>
            ) : (
                <p className="text-xs text-red-600 py-8 text-center">
                    Failed to load diagnostic. Try refreshing.
                </p>
            )}
        </div>
    );
}
